/**
 * DVL - cart terms gate
 * =====================
 * Blocks every route out of the cart until "I agree to the terms" is ticked — the checkout button
 * AND the express wallets (Shop Pay, PayPal, Google Pay) — and records consent as the
 * `Agreed to the terms, conditions and policies on` cart attribute, stamped with the moment of the tick. Shopify carries cart
 * attributes through to the order.
 *
 * Replaces the Effective Apps T&C app (tnc-app.herokuapp.com / app.tncapp.com), a third-party
 * script tag sitting in the checkout path that shipped the box PRE-TICKED with required=false.
 *
 * How consent gets onto the cart
 * ------------------------------
 * The checkbox is named `attributes[Terms accepted]`, so a NATIVE form post carries it. That is not
 * enough on its own: Prestige navigates to checkout in JavaScript without posting the form (verified
 * — a ticked box reached checkout with an EMPTY cart.attributes), and the express wallets never post
 * the form at all. So the attribute is written through /cart/update.js the moment the box changes,
 * and nothing lets the customer out of the cart until that write has landed.
 *
 * Express wallets
 * ---------------
 * The wallet row is <shopify-accelerated-checkout-cart>, rendered asynchronously by Shopify; PayPal
 * sits in a cross-origin iframe, so its clicks cannot be intercepted by listening for them. Instead,
 * while consent is missing, a transparent shield is laid over each wallet container: it catches the
 * click, flags the terms box and scrolls it into view. The wallets are dimmed to show they are
 * locked. A MutationObserver re-applies this whenever Shopify injects the wallets or the theme
 * re-renders its drawer.
 *
 * Why JS rather than the `required` attribute
 * -------------------------------------------
 *   - `required` blocks EVERY submit of the form, including Fashionopolism's hidden name="update"
 *     submit, so a quantity update would fail validation.
 *   - Prestige's cart forms carry `novalidate`, which disables native validation outright.
 *
 * Listener ordering is load-bearing
 * ---------------------------------
 * The submit listener is on WINDOW in the CAPTURE phase — the very first stop an event makes — and
 * blocks with stopImmediatePropagation. Fashionopolism's theme.js registers its own submit handler
 * on document/capture, runs first because it loads first, calls stopImmediatePropagation and
 * navigates to checkout itself, so a document-level gate never ran at all on seafrogs (verified: an
 * unticked submit reached /checkouts/). Being delegated, it also survives Fashionopolism cloning the
 * cart form into its drawer.
 *
 * Loading, and themes that rebuild their cart
 * ---------------------------------------------
 * This script must not depend on the snippet for its own loading: with an EMPTY cart at page load
 * the cart surfaces do not render the snippet, and markup injected later by an AJAX cart update
 * never executes its <script> tags. So every theme's layout loads this file on every page.
 * (Verified: before that, an empty-cart shopper who pressed "Add to cart" could check out unticked
 * on saltedline and saltedlineeurope.) Some themes also rebuild the checkout area in JavaScript —
 * Galleria wipes its modal cart and appends a hard-coded checkout row with no terms box — so any
 * cart form that has a checkout button but no box gets one re-inserted, cloned from a
 * <template id="dvl-cart-terms-template"> that the layout renders on every page.
 *
 * Fails open: with JS off, or if the attribute write fails, checkout still works. That is the safer
 * failure for a storefront, and it is what the app being replaced did. (The wallets need JS to render
 * at all, so there is nothing to bypass with it off.)
 *
 * Ticked by default (2026-10-05)
 * ------------------------------
 * The snippet renders the box ticked; the store owner chose that after an unticked box was suspected
 * of costing orders. A box that is ticked but carries no `data-dvl-given` has consent that is not on
 * the cart yet, so it is written straight away, which unlocks the wallets without a click. Unticking still
 * clears the attribute and locks everything, exactly as before.
 *
 * Measuring it
 * ------------
 * Three events go to EQA analytics through the custom pixel (no-ops without one):
 *   dvl_terms_blocked  - tried to leave the cart unticked; surface "checkout" or "wallet"
 *   dvl_terms_unticked - took the default tick off
 *   dvl_terms_ticked   - ticked it (again)
 */
(function () {
  'use strict';

  if (window.__dvlCartTermsReady) return; // the snippet can render once per cart surface
  window.__dvlCartTermsReady = true;

  var INPUT = '.dvl-cart-terms__input';
  // The KEY is the label Shopify prints on the order, so it is phrased as a statement.
  var ATTRIBUTE = 'Agreed to the terms, conditions and policies on';
  var LEGACY_ATTRIBUTE = 'Terms accepted'; // pre-rename carts; cleared on write so no order shows both
  // The regular <div> wrapping Shopify's wallet element. The shield goes inside this rather than
  // inside <shopify-accelerated-checkout-cart>, whose shadow root would not render it.
  var WALLETS = '[data-shopify="dynamic-checkout-cart"], .dynamic-checkout__content';

  // One consent for the whole page, whichever copy of the box it was given in.
  //   checked - the customer's choice
  //   saved   - that choice is on the cart (nothing may leave the cart until it is)
  //   touched - the customer has changed the box on this page; from then on their choice rules
  var state = { checked: false, value: '', saved: false, saving: null, touched: false };

  function boxes() { return Array.prototype.slice.call(document.querySelectorAll(INPUT)); }
  function visible(el) { return !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length); }

  function root() {
    // respects market / locale path prefixes such as /en-de/
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function stamp() {
    return new Date().toISOString().slice(0, 16).replace('T', ' ') + ' UTC';
  }

  // Into Shopify's customer-events bus, which the EQA pixel forwards. Never allowed to break the gate.
  function track(name, surface) {
    try {
      if (window.Shopify && Shopify.analytics && Shopify.analytics.publish) {
        Shopify.analytics.publish(name, { surface: surface || 'cart' });
      }
    } catch (e) {}
  }

  function setError(box, on) {
    var wrap = box.closest && box.closest('.dvl-cart-terms');
    if (!wrap) return;
    wrap.classList.toggle('dvl-cart-terms--error', on);
    var alert = wrap.querySelector('.dvl-cart-terms__alert');
    if (alert) alert.hidden = !on;
  }

  // Point the customer at the box: the first visible copy, so it works from the cart page or drawer.
  function demandConsent() {
    var all = boxes();
    all.forEach(function (b) { setError(b, true); });
    var target = all.filter(visible)[0] || all[0];
    if (!target) return;
    try { target.scrollIntoView({ block: 'center', behavior: 'smooth' }); } catch (e) {}
    try { target.focus({ preventScroll: true }); } catch (e) {}
  }

  // Write (or clear) the attribute on the cart. Resolves either way — a failed write must never
  // block a purchase; the native form field is still there as a fallback.
  function persist() {
    var attributes = {};
    attributes[ATTRIBUTE] = state.checked ? state.value : '';
    attributes[LEGACY_ATTRIBUTE] = ''; // drop the old key rather than let the order carry both
    state.saved = false;
    var want = state.checked;
    state.saving = fetch(root() + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: attributes })
    }).then(function (r) { return r.ok; }, function () { return false; })
      .then(function (ok) {
        // A newer change may have superseded this write; only the latest one settles the state.
        if (state.checked === want) { state.saved = true; state.saving = null; refresh(); }
        return ok;
      });
    refresh(); // lock the wallets for the duration of the write
    return state.saving;
  }

  function makeShield() {
    var shield = document.createElement('button');
    shield.type = 'button';
    shield.className = 'dvl-terms-shield';
    // Reuse the alert text the snippet already rendered in the shop's language.
    var alert = document.querySelector('.dvl-cart-terms__alert');
    var label = alert ? alert.textContent.trim() : 'Please agree to the terms and conditions first.';
    shield.setAttribute('aria-label', label);
    shield.title = label;
    return shield;
  }

  // Re-insert the box into any cart form whose checkout area a theme rebuilt without it (see the
  // header). It goes just before the checkout button's container, which is where the snippet sits
  // in the server-rendered markup, so the theme's own layout and alignment rules still apply.
  var template = null;
  function heal() {
    if (!template) {
      var t = document.getElementById('dvl-cart-terms-template');
      template = t && t.content ? t.content.querySelector('.dvl-cart-terms') : null;
      if (!template) return; // no template on this theme: nothing needs rebuilding here
    }
    Array.prototype.forEach.call(document.querySelectorAll('form'), function (form) {
      var btn = form.querySelector('[name="checkout"]');
      if (!btn || form.querySelector(INPUT)) return;
      var anchor = btn.parentElement && btn.parentElement !== form ? btn.parentElement : btn;
      anchor.parentNode.insertBefore(template.cloneNode(true), anchor);
    });
  }

  // Bring every wallet row and every copy of the box in line with the current state.
  function refresh() {
    heal();
    var all = boxes();

    // A box rendered ticked is either consent already on the cart (`data-dvl-given`) or the default
    // tick, whose consent is written now. Only inferred until the customer touches the box:
    // defaultChecked keeps reflecting the server-rendered HTML even after they untick, so without
    // the `touched` guard an untick was re-ticked the moment its clearing write came back (verified
    // on the seafrogs preview).
    if (!state.touched && !state.checked && !state.saving) {
      var ticked = all.filter(function (b) { return b.defaultChecked; });
      var given = ticked.filter(function (b) { return b.dataset.dvlGiven === '1'; })[0];
      if (given) { state.checked = true; state.value = given.value; state.saved = true; }
      else if (ticked.length) { state.checked = true; state.value = stamp(); persist(); return; }
    }

    // Keep every copy in step — e.g. a drawer re-rendered after a quantity change.
    all.forEach(function (b) {
      if (b.checked !== state.checked) b.checked = state.checked;
      if (state.checked && state.value && b.value !== state.value) b.value = state.value;
    });

    // Only guard wallets where there is a box to tick; with none on the page there is no way to
    // consent, and locking the wallets would just break the purchase.
    var locked = all.length > 0 && !(state.checked && state.saved);
    Array.prototype.forEach.call(document.querySelectorAll(WALLETS), function (wrap) {
      var shield = wrap.querySelector(':scope > .dvl-terms-shield');
      if (locked) {
        if (!shield) wrap.appendChild(makeShield());
        wrap.classList.add('dvl-terms-locked');
      } else {
        if (shield) shield.remove();
        wrap.classList.remove('dvl-terms-locked');
      }
    });
  }

  // ---- the checkout button --------------------------------------------------------------------

  window.addEventListener('submit', function (event) {
    var form = event.target;
    if (!form || !form.querySelector) return;
    var box = form.querySelector(INPUT);
    if (!box) return;

    // Gate the checkout button only. A submitter with another name (update) is left alone; no
    // submitter at all (Enter in a field) is gated, because that submits the default button.
    var submitter = event.submitter;
    if (submitter && submitter.name && submitter.name !== 'checkout') return;

    if (!box.checked) {
      event.preventDefault();
      event.stopImmediatePropagation(); // keep the themes' own handlers from navigating anyway
      setError(box, true);
      try { box.focus(); } catch (e) {}
      track('dvl_terms_blocked', 'checkout');
      return;
    }

    if (state.checked && state.saved) return; // consent is on the cart: carry on normally

    // Ticked but not yet on the cart: hold the submit, make sure the attribute lands, re-submit.
    event.preventDefault();
    event.stopImmediatePropagation();
    if (!state.checked) { state.checked = true; state.value = box.value || stamp(); }
    (state.saving || persist()).then(function () {
      state.saved = true; // even if the write failed: fail open rather than loop
      if (form.requestSubmit) form.requestSubmit(submitter || undefined);
      else form.submit();
    });
  }, true);

  // Themes that navigate to checkout in JavaScript never fire a submit event: Fashionopolism's
  // theme.js answers the button's CLICK and calls form.submit(), which by definition skips the
  // submit event (verified live on seafrogs — a click reached /checkouts/ with the box unticked,
  // while the same form submitted through requestSubmit was blocked). So the button is gated on
  // click as well, in the capture phase, before the theme's own handler runs.
  window.addEventListener('click', function (event) {
    var btn = event.target && event.target.closest && event.target.closest('[name="checkout"]');
    if (!btn || btn.dataset.dvlPass === '1') return;
    var form = btn.form || (btn.closest && btn.closest('form'));
    var box = form && form.querySelector(INPUT);
    if (!box) return;

    if (!box.checked) {
      event.preventDefault();
      event.stopImmediatePropagation();
      setError(box, true);
      try { box.focus(); } catch (e) {}
      track('dvl_terms_blocked', 'checkout');
      return;
    }

    if (state.checked && state.saved) return; // consent is on the cart: carry on normally

    // ticked but not yet recorded: hold the click, let the write land, then click through
    event.preventDefault();
    event.stopImmediatePropagation();
    if (!state.checked) { state.checked = true; state.value = box.value || stamp(); }
    (state.saving || persist()).then(function () {
      state.saved = true; // even if the write failed: fail open rather than loop
      btn.dataset.dvlPass = '1';
      btn.click();
      setTimeout(function () { delete btn.dataset.dvlPass; }, 0);
    });
  }, true);

  // ---- the express wallets ----------------------------------------------------------------------

  // Capture phase on window again, so a shield click is handled before anything else sees it.
  window.addEventListener('click', function (event) {
    var shield = event.target && event.target.closest && event.target.closest('.dvl-terms-shield');
    if (!shield) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    demandConsent();
    track('dvl_terms_blocked', 'wallet');
  }, true);

  // ---- the box ----------------------------------------------------------------------------------

  document.addEventListener('change', function (event) {
    var box = event.target;
    if (!box || !box.matches || !box.matches(INPUT)) return;
    state.touched = true;
    state.checked = box.checked;
    // Record when consent was given, not when the page happened to render.
    state.value = box.checked ? stamp() : '';
    if (box.checked) boxes().forEach(function (b) { setError(b, false); });
    track(box.checked ? 'dvl_terms_ticked' : 'dvl_terms_unticked');
    persist();
  });

  // Shopify injects the wallets asynchronously and the theme re-renders its drawer, so re-apply the
  // shields whenever the DOM changes. Coalesced to one pass per frame; the shield insert itself
  // triggers a pass, which finds nothing to do.
  var queued = false;
  new MutationObserver(function () {
    if (queued) return;
    queued = true;
    requestAnimationFrame(function () { queued = false; refresh(); });
  }).observe(document.documentElement, { childList: true, subtree: true });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', refresh);
  else refresh();
})();
